import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { FiArrowLeft, FiMail, FiPhone, FiMapPin, FiBriefcase, FiUser, FiGlobe, FiCheckCircle, FiEdit } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getEmailManagementById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { getEmailListItemLabel } from '../../constants/emailListItems';
import './AdminEmailDetail.css';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminEmailDetail = () => {
    const navigate = useNavigate();
    const { emailManagementId } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [emailManagement, setEmailManagement] = useState(null);

    useEffect(() => {
        const fetchEmailManagement = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getEmailManagementById(emailManagementId);
                // API peut retourner { email_management: { ... }, user: { ... } } ou autre structure
                setEmailManagement(data?.email_management || data?.data || data || null);
            } catch (e) {
                setError(handleApiError(e, 'Erreur lors du chargement de l\'email management'));
            } finally {
                setLoading(false);
            }
        };
        if (emailManagementId) fetchEmailManagement();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailManagementId]);

    if (loading) {
        return (
            <div className="dashboard-page admin-email-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de l'Email Management</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement de l'email management...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page admin-email-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de l'Email Management</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">{error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!emailManagement) {
        return (
            <div className="dashboard-page admin-email-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de l'Email Management</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée d'email management disponible pour l'identifiant: {emailManagementId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return dateString;
        }
    };

    const user = emailManagement.user || {};
    const emailMgmt = emailManagement.email_management || emailManagement;

    return (
        <div className="dashboard-page admin-email-detail-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiMail className="me-2" />
                    Détail de l'Email Management
                </h2>
                <div className="d-flex gap-2">
                    <Button variant="primary" onClick={() => navigate(`/admin/email/edit/${emailManagementId}`)}>
                        <FiEdit className="me-2" />
                        Modifier
                    </Button>
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        <FiArrowLeft className="me-2" /> Retour
                    </Button>
                </div>
            </div>

            <Section title="Informations principales">
                <Row>
                    <Col md={6} className="mb-3">
                        <Label>Email Management ID</Label>
                        <Value>{emailMgmt.email_management_id || 'N/A'}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Processus Email</Label>
                        <Value>
                            <Badge bg="info">{emailMgmt.email_process || 'N/A'}</Badge>
                        </Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Date de création</Label>
                        <Value>{formatDate(emailMgmt.created_at)}</Value>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Label>Dernière modification</Label>
                        <Value>{formatDate(emailMgmt.updated_at)}</Value>
                    </Col>
                </Row>
            </Section>

            <Section title="Liste des e-mails autorisés">
                <div>
                    {emailMgmt.email_list && Array.isArray(emailMgmt.email_list) && emailMgmt.email_list.length > 0 ? (
                        <ul className="list-unstyled">
                            {emailMgmt.email_list.map((email, index) => (
                                <li key={`${email}-${index}`} className="mb-2">
                                    <Badge bg="success" className="me-2">
                                        <FiMail className="me-1" />
                                        {getEmailListItemLabel(email)}
                                    </Badge>
                                    <code className="small text-muted">{email}</code>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-muted">Aucun code e-mail dans la liste</p>
                    )}
                </div>
            </Section>

            {user && Object.keys(user).length > 0 && (
                <Section title="Informations du Partenaire">
                    <Row>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiUser className="me-1" />
                                Prénom
                            </Label>
                            <Value>{user.first_name || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiUser className="me-1" />
                                Nom
                            </Label>
                            <Value>{user.last_name || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiMail className="me-1" />
                                Email
                            </Label>
                            <Value>{user.email || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiPhone className="me-1" />
                                Téléphone
                            </Label>
                            <Value>{user.phone_number || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiBriefcase className="me-1" />
                                Entreprise
                            </Label>
                            <Value>{user.company_name || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiMapPin className="me-1" />
                                Ville
                            </Label>
                            <Value>{user.city || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiGlobe className="me-1" />
                                Pays
                            </Label>
                            <Value>{user.country || 'N/A'}</Value>
                        </Col>
                        <Col md={6} className="mb-3">
                            <Label>
                                <FiCheckCircle className="me-1" />
                                Statut
                            </Label>
                            <Value>
                                {user.is_verified ? (
                                    <Badge bg="success">Vérifié</Badge>
                                ) : (
                                    <Badge bg="warning">Non vérifié</Badge>
                                )}
                            </Value>
                        </Col>
                    </Row>
                </Section>
            )}
        </div>
    );
};

export default AdminEmailDetail;


